import { Injectable, NgZone } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { ServerEvent } from './events';

export class EventSourceFake {
  private listeners: Function[] = [];

  addEventListener(eventName: string, cb: Function) {
    this.listeners.push(cb);
  };

  onerror(error: any) { }

  // Helper method for emitting fake events
  emit(data: any) {
    this.listeners.forEach(cb => cb(data))
  };

  close() {
    this.listeners = [];
  }
}

@Injectable({
  providedIn: 'root'
})
export class EventSourceService {
  private realEventSource: EventSource | null = null;
  private fakeEventSource: EventSourceFake | null = null;
  readonly options: EventSourceInit = { withCredentials: false };

  /**
   * constructor
   * @param zone - we need to use zone while working with server-sent events
   * because it's an asynchronous operations which are run outside of change detection scope
   * and we need to notify Angular about changes related to SSE events
   */
  constructor(private zone: NgZone) { }

  /**
   * Open a real EventSource connection. Idempotent — returns the existing
   * connection if one is already open.
   */
  connect(url: string, token: string | null): EventSource {
    if (this.realEventSource && this.realEventSource.readyState !== EventSource.CLOSED) {
      return this.realEventSource;
    }
    const fullUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    this.realEventSource = new EventSource(fullUrl, this.options);
    return this.realEventSource;
  }

  /** Close the real connection. Fake source is independent. */
  disconnect(): void {
    this.realEventSource?.close();
    this.realEventSource = null;
  }

  /**
   * Method for resolving the EventSource instance.
   * In fake mode a new EventSourceFake is created on each call (legacy behavior
   * for optimistic UI updates).
   */
  getEventSource(fake?: boolean): EventSource | EventSourceFake {
    if (fake) {
      this.fakeEventSource = new EventSourceFake();
      return this.fakeEventSource;
    }
    if (!this.realEventSource) {
      throw new Error('EventSource has not been initialized. Call connect() first.');
    }
    return this.realEventSource;
  }

  /**
   * Subscribe to one or more named server events.
   * Returns an Observable; unsubscribing removes the listeners from the source.
   */
  listenServerEvents(eventNames: string[] = [], fake?: boolean): Observable<{ name: string; data: Event }> {
    const source = this.getEventSource(fake);

    return new Observable((subscriber: Subscriber<{ name: string; data: Event }>) => {
      source.onerror = (error: any) => {
        this.zone.run(() => subscriber.error(error));
      };

      const handlers: { name: string; cb: (event: Event) => void }[] = [];
      eventNames.forEach((event: string) => {
        const cb = (data: Event) => {
          this.zone.run(() => subscriber.next({ name: event, data }));
        };
        source.addEventListener(event, cb);
        handlers.push({ name: event, cb });
      });

      return () => {
        if (typeof (source as EventSource).removeEventListener === 'function') {
          handlers.forEach(({ name, cb }) =>
            (source as EventSource).removeEventListener(name, cb)
          );
        }
      };
    });
  }

  readonly publishFakeEvent = (data: { event: ServerEvent;[key: string]: unknown; }): void => this.fakeEventSource?.emit(data);

  close(): void {
    this.disconnect();
    this.fakeEventSource?.close();
    this.fakeEventSource = null;
  }
}
